import { useMemo } from "react";
import { KeyRound } from "lucide-react";

import {
  useApiKeysQuery,
  useDeactivatedApiKeysQuery,
} from "@/hooks/use-api-keys";
import { formatDate } from "@/lib/utils";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/empty-state";
import { EntityButton } from "@/components/entity-button";
import { TableSkeleton } from "@/components/loading-skeletons";
import { PageHeader } from "@/components/layout/page-header";
import { RowLink, TableLinkRow } from "@/components/table-link-row";

export function ApiKeysPage() {
  const { data: apiKeys, isLoading, error } = useApiKeysQuery();
  const { data: deactivatedApiKeys } = useDeactivatedApiKeysQuery();

  const sortedApiKeys = useMemo(() => {
    if (!apiKeys) return [];
    return [...apiKeys].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [apiKeys]);

  const sortedDeactivatedKeys = useMemo(() => {
    if (!deactivatedApiKeys) return [];
    return [...deactivatedApiKeys].sort(
      (a, b) =>
        new Date(b.deactivated_at ?? b.created_at).getTime() -
        new Date(a.deactivated_at ?? a.created_at).getTime()
    );
  }, [deactivatedApiKeys]);

  return (
    <div className="flex flex-1 flex-col gap-6">
      <PageHeader
        title="API Keys"
        actions={
          apiKeys && <EntityButton entityName="API Key" to="/settings/api-keys/new" />
        }
      />

      {isLoading && <TableSkeleton columns={2} />}

      {error && (
        <p className="py-16 text-center text-sm text-destructive">
          Failed to load API keys
        </p>
      )}

      {apiKeys && (
        <>
          {sortedApiKeys.length === 0 && (
            <EmptyState
              icon={KeyRound}
              title="No API keys found"
              description="API keys you create will appear here."
            >
              <EntityButton entityName="API Key" to="/settings/api-keys/new" />
            </EmptyState>
          )}

          {sortedApiKeys.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="md:w-48">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedApiKeys.map((apiKey) => (
                  <TableLinkRow key={apiKey.id} to={`/settings/api-keys/${apiKey.id}`}>
                    <TableCell>
                      <RowLink to={`/settings/api-keys/${apiKey.id}`}>
                        {apiKey.name}
                      </RowLink>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatDate(apiKey.created_at)}
                    </TableCell>
                  </TableLinkRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}

      {sortedDeactivatedKeys.length > 0 && (
        <div className="mt-4 flex flex-col gap-3">
          <h2 className="text-sm font-medium text-muted-foreground">
            Deactivated API Keys
          </h2>
          <Table className="opacity-60">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="md:w-64">Key</TableHead>
                <TableHead className="md:w-48">Deactivated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedDeactivatedKeys.map((apiKey) => (
                <TableRow key={apiKey.id} className="hover:bg-transparent">
                  <TableCell className="font-medium">{apiKey.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {apiKey.api_key}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {apiKey.deactivated_at
                      ? formatDate(apiKey.deactivated_at)
                      : "Unknown"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
